import { useEffect, useRef, useState } from 'react';
import { CheckCircle2Icon, LightbulbIcon, PlayIcon, XCircleIcon } from 'lucide-react';
import type { VideoQuestion } from '../../pages/ELearnings.js';
import { readDraft, writeDraft } from './draftStorage.js';

const OPTION_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

interface Kijkstand {
  answers: Record<string, number>;
  /** Verste punt dat de cursist zelf heeft afgespeeld; vooruitspoelen stopt hier. */
  maxWatched: number;
  ended: boolean;
}

/**
 * Lesvideo die op elk vraagmoment pauzeert en pas verder speelt nadat de
 * cursist een antwoord heeft aangeklikt. Fout antwoorden blokkeert niet; het
 * goede antwoord en de toelichting worden direct getoond. Vooruitspoelen voorbij
 * het verst bekeken punt of een onbeantwoorde vraag kan niet, zodat afronden
 * betekent dat de hele training is doorlopen.
 *
 * Vanaf sm ligt de vraag als overlay over de video; op een telefoon is de video
 * daarvoor te klein en staat de vraag eronder. De kijkstand staat in
 * localStorage zodat een herladen pagina de cursist niet terug naar 0:00 stuurt.
 */
export function VideoMetVragen({
  src,
  questions,
  storageKey,
  free,
  serverAnswers,
  onAnswer,
  onChange,
}: {
  src: string;
  questions: VideoQuestion[];
  storageKey: string;
  /** Les al afgerond: vrij kijken en spoelen, vragen niet opnieuw. */
  free: boolean;
  /** Antwoorden die de server al heeft (bv. gegeven op een ander apparaat). */
  serverAnswers: Record<string, number>;
  /** Elk nieuw antwoord direct naar de server (voortgang + bewijs). */
  onAnswer: (questionId: string, optionIndex: number) => void;
  onChange: (answers: Record<string, number>, done: boolean) => void;
}): JSX.Element {
  const wrapRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [stand, setStand] = useState<Kijkstand>(() => {
    const local = readDraft<Kijkstand>(storageKey) ?? { answers: {}, maxWatched: 0, ended: false };
    // Op een ander apparaat begonnen: hervat na de laatst beantwoorde vraag.
    const lastAnswered = questions
      .filter((q) => q.id in serverAnswers)
      .reduce((max, q) => Math.max(max, q.videoSeconds), 0);
    return {
      ...local,
      answers: { ...serverAnswers, ...local.answers },
      maxWatched: Math.max(local.maxWatched, lastAnswered),
    };
  });
  const [active, setActive] = useState<VideoQuestion | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Schermvullend toont de overlay ook op een telefoon: de kaart onder de
  // video valt dan buiten beeld en de cursist zou vastzitten.
  const [fullscreen, setFullscreen] = useState(false);
  const resumed = useRef(false);

  const nextCue = free ? undefined : questions.find((q) => stand.answers[q.id] === undefined);
  const done = questions.every((q) => stand.answers[q.id] !== undefined) && stand.ended;

  useEffect(() => {
    writeDraft(storageKey, stand);
    onChange(stand.answers, done);
  }, [storageKey, stand, done, onChange]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  // timeupdate vuurt maar ~4x per seconde: te grof, dan staat het antwoord al
  // in beeld. Tijdens afspelen daarom per frame controleren.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !nextCue || active) return;
    let raf = 0;
    const tick = (): void => {
      if (v.currentTime >= nextCue.videoSeconds) stopAt(nextCue);
      else raf = requestAnimationFrame(tick);
    };
    const start = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    v.addEventListener('play', start);
    if (!v.paused) start();
    return () => {
      cancelAnimationFrame(raf);
      v.removeEventListener('play', start);
    };
  }, [nextCue, active]);

  // De fullscreenknop van de speler maakt alleen <video> schermvullend, en dan
  // valt de overlay weg. Zet daarom de omhullende div schermvullend.
  useEffect(() => {
    const onFs = (): void => {
      setFullscreen(document.fullscreenElement === wrapRef.current);
      if (document.fullscreenElement === videoRef.current && wrapRef.current) {
        void document
          .exitFullscreen()
          .then(() => wrapRef.current?.requestFullscreen())
          .catch(() => undefined);
      }
    };
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  function stopAt(q: VideoQuestion): void {
    const v = videoRef.current!;
    v.pause();
    v.currentTime = q.videoSeconds;
    // iOS speelt schermvullend af in de eigen speler; daar kan niets overheen.
    const ios = v as HTMLVideoElement & { webkitExitFullscreen?: () => void };
    ios.webkitExitFullscreen?.();
    setActive(q);
  }

  function handleTimeUpdate(): void {
    const v = videoRef.current!;
    // Vangnet: in een achtergrondtab draait requestAnimationFrame niet.
    if (nextCue && !active && v.currentTime >= nextCue.videoSeconds) {
      stopAt(nextCue);
      return;
    }
    if (!v.seeking && v.currentTime > stand.maxWatched) {
      setStand((s) => ({ ...s, maxWatched: v.currentTime }));
    }
  }

  function handleSeeking(): void {
    if (free) return;
    const v = videoRef.current!;
    const limit = Math.min(stand.maxWatched, nextCue?.videoSeconds ?? Infinity);
    if (v.currentTime > limit + 1) {
      v.currentTime = limit;
      setNotice('Vooruitspoelen kan tot waar je al gekeken hebt. Terugspoelen kan altijd.');
    }
  }

  function handleLoaded(): void {
    // Hervat waar de cursist gebleven was (maar niet voorbij een open vraag).
    if (resumed.current || free) return;
    resumed.current = true;
    const v = videoRef.current!;
    const start = Math.min(stand.maxWatched, nextCue?.videoSeconds ?? Infinity);
    if (start > 0) v.currentTime = start;
  }

  function answer(index: number): void {
    if (!active || stand.answers[active.id] !== undefined) return;
    setStand((s) => ({ ...s, answers: { ...s.answers, [active.id]: index } }));
    onAnswer(active.id, index);
  }

  function verder(): void {
    setActive(null);
    void videoRef.current?.play();
  }

  const picked = active ? stand.answers[active.id] : undefined;
  const answered = picked !== undefined;
  const beantwoord = questions.filter((q) => stand.answers[q.id] !== undefined).length;

  return (
    <div>
      <div
        ref={wrapRef}
        className="relative grid overflow-hidden rounded-xl bg-black [&:fullscreen]:content-center [&:fullscreen]:overflow-y-auto [&:fullscreen]:rounded-none"
      >
        <video
          ref={videoRef}
          controls
          // Zonder playsInline opent een iPhone elke video in de eigen
          // schermvullende speler, waar de vraag niet overheen kan.
          playsInline
          // Geen downloadknop en geen rechtermuismenu ('Video opslaan als').
          // Drempel, geen DRM: de signed URL blijft met devtools op te halen.
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
          preload="metadata"
          src={src}
          onLoadedMetadata={handleLoaded}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onPlay={() => {
            if (active && !answered) videoRef.current?.pause();
          }}
          onEnded={() => setStand((s) => ({ ...s, ended: true }))}
          className="col-start-1 row-start-1 aspect-video w-full self-center bg-black"
        />

        {/* Grid-stapeling i.p.v. absolute positie: past de vraag niet binnen de
            video, dan groeit het donkere vlak mee en hoeft niemand te scrollen. */}
        {active && (
          <div
            className={`col-start-1 row-start-1 ${
              fullscreen
                ? 'flex items-center justify-center bg-ink-900/70 p-4 backdrop-blur-sm'
                : 'hidden sm:flex sm:items-center sm:justify-center sm:bg-ink-900/70 sm:p-4 sm:backdrop-blur-sm'
            }`}
          >
            <VraagKaart
              question={active}
              index={questions.indexOf(active)}
              total={questions.length}
              picked={picked}
              onAnswer={answer}
              onVerder={verder}
              compact
            />
          </div>
        )}
        {notice && (
          <p
            role="status"
            className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-lg bg-ink-900/85 px-3 py-1.5 text-[12px] text-white"
          >
            {notice}
          </p>
        )}
      </div>

      {active && (
        <div className="mt-4 sm:hidden">
          <VraagKaart
            question={active}
            index={questions.indexOf(active)}
            total={questions.length}
            picked={picked}
            onAnswer={answer}
            onVerder={verder}
          />
        </div>
      )}

      {!free && questions.length > 0 && (
        <p className="mt-2 text-[12px] text-ink-900/50">
          De video stopt bij {questions.length} vragen. Beantwoord: {beantwoord} van{' '}
          {questions.length}.
        </p>
      )}
    </div>
  );
}

function VraagKaart({
  question,
  index,
  total,
  picked,
  onAnswer,
  onVerder,
  compact = false,
}: {
  question: VideoQuestion;
  index: number;
  total: number;
  picked: number | undefined;
  onAnswer: (index: number) => void;
  onVerder: () => void;
  /** Overlay: kleiner lettertype zodat de kaart binnen de video past. */
  compact?: boolean;
}): JSX.Element {
  const answered = picked !== undefined;
  const correct = answered && question.answerOptions[picked]?.isCorrect;
  const text = 'text-[13px]';
  return (
    <div
      role="group"
      aria-label="Vraag bij de video"
      className={`w-full rounded-2xl border-2 border-brand-200 bg-white shadow-lg ${
        compact ? 'my-auto max-w-xl p-4' : 'p-5'
      }`}
    >
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-brand-700">
        Vraag {index + 1} van {total}
      </p>
      <h2
        className={`${compact ? 'text-[14px]' : 'text-[15px]'} font-bold leading-snug text-ink-900`}
      >
        {question.title}
      </h2>
      {question.questionText && !answered && (
        <p className={`mt-1 ${text} text-ink-900/65`}>{question.questionText}</p>
      )}
      <div className={compact ? 'mt-3 space-y-1.5' : 'mt-4 space-y-2.5'}>
        {question.answerOptions.map((option, i) => {
          const showCorrect = answered && option.isCorrect;
          const showWrong = answered && picked === i && !option.isCorrect;
          // Na het antwoord alleen de gekozen en de juiste optie: zo past de
          // kaart met toelichting binnen de video.
          if (answered && !showCorrect && !showWrong) return null;
          const border = showCorrect
            ? 'border-green-500 bg-green-50'
            : showWrong
              ? 'border-red-500 bg-red-50'
              : 'border-brand-100 bg-white hover:border-brand-300';
          return (
            <button
              key={i}
              type="button"
              disabled={answered}
              onClick={() => onAnswer(i)}
              className={`flex w-full items-start gap-3 rounded-lg border-2 text-left transition-colors ${border} ${
                compact ? 'p-2' : 'p-3'
              } ${answered ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                  showCorrect
                    ? 'bg-green-500 text-white'
                    : showWrong
                      ? 'bg-red-500 text-white'
                      : 'bg-brand-50 text-ink-900/60'
                }`}
              >
                {OPTION_LETTERS[i]}
              </span>
              <span className={`flex-1 pt-0.5 ${text} text-ink-900`}>{option.text}</span>
              {showCorrect && (
                <CheckCircle2Icon size={17} className="mt-0.5 shrink-0 text-green-500" />
              )}
              {showWrong && <XCircleIcon size={17} className="mt-0.5 shrink-0 text-red-500" />}
            </button>
          );
        })}
      </div>

      {answered && (
        <div className="mt-2.5 space-y-2">
          {question.explanation && (
            <div className="flex items-start gap-2 rounded-lg border border-petrol-100 bg-petrol-50 px-3 py-2">
              <LightbulbIcon size={16} className="mt-0.5 shrink-0 text-petrol-500" />
              <p className={`${text} text-ink-900/70`}>{question.explanation}</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <p className={`${text} font-semibold ${correct ? 'text-green-700' : 'text-red-700'}`}>
              {correct ? 'Goed!' : 'Helaas, dat is niet juist.'}
            </p>
            <button
              type="button"
              onClick={onVerder}
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-brand-600 px-4 py-2 text-[14px] font-semibold text-white transition-colors hover:bg-brand-700"
            >
              <PlayIcon size={16} />
              Verder kijken
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
